/**
 * Deep link to in-app path.
 *
 * App Store in-app event cards (scripts/asc-events, docs/asc-in-app-events.md)
 * can carry a deep link, and Apple checks that it lands on the event's
 * content. Until now the app routed only friend invites (lib/friendInvite.ts),
 * so the cards shipped without one. This maps the URLs the OS hands to
 * Capacitor's appUrlOpen onto a wouter path:
 *
 *   nexusdex://calendar           -> /calendar
 *   https://nexusdex.ai/calendar  -> /calendar   (universal-link form)
 *
 * Anything it does not know returns null, so DeepLinkHandler falls through to
 * the friend-invite redemption exactly as before. Pure over the URL string so
 * the shape edge cases (case, slashes, query, fragment) are tested without a
 * Capacitor runtime. A query string is kept on the in-app path so a later card
 * can point at one event (nexusdex://calendar?event=<id>) without touching
 * this file; the Calendar reads nothing from it today.
 */

/** The custom scheme registered in ios/App/App/Info.plist and the Android manifest. */
const CUSTOM_SCHEME = "nexusdex";

/** Hosts the Associated Domains entitlement (applinks:nexusdex.ai) hands us. */
const UNIVERSAL_LINK_HOSTS: ReadonlySet<string> = new Set(["nexusdex.ai", "www.nexusdex.ai"]);

/**
 * Deep-link page name (lower-case, no slashes) to in-app route. A Map, not an
 * object, so `nexusdex://constructor` cannot read a prototype member. Add a
 * row here and, for its https form, the path to the AASA served by the
 * nexusdex-infopage repo.
 */
const PAGE_ROUTES: ReadonlyMap<string, string> = new Map([
  ["calendar", "/calendar"],
]);

/** scheme://hostAndPath?search#fragment, with the three tails optional. */
const URL_SHAPE = /^([a-z][a-z0-9+.-]*):\/\/([^?#]*)(\?[^#]*)?(#.*)?$/i;

/**
 * The in-app path a deep link opens, or null when the URL is not a page deep
 * link (friend invites, other hosts, other schemes, unknown pages, junk).
 */
export function deepLinkRoute(url: string): string | null {
  const m = URL_SHAPE.exec(url.trim());
  if (!m) return null;
  const scheme = m[1].toLowerCase();
  const hostAndPath = m[2];
  // A bare "?" carries nothing worth keeping.
  const search = m[3] && m[3].length > 1 ? m[3] : "";

  let page: string;
  if (scheme === CUSTOM_SCHEME) {
    // nexusdex://calendar : what a URL parser would call the host IS the page.
    page = hostAndPath;
  } else if (scheme === "https" || scheme === "http") {
    const slash = hostAndPath.indexOf("/");
    const host = (slash === -1 ? hostAndPath : hostAndPath.slice(0, slash)).toLowerCase();
    if (!UNIVERSAL_LINK_HOSTS.has(host)) return null;
    page = slash === -1 ? "" : hostAndPath.slice(slash + 1);
  } else {
    return null;
  }

  page = page.replace(/^\/+|\/+$/g, "").toLowerCase();
  const route = PAGE_ROUTES.get(page);
  if (!route) return null;
  return `${route}${search}`;
}
